// Assessments tab: the region's assessments, newest first (name, product, grid code with the "Newer grid-code version" warning, plant type, status with live run progress, review progress, approver or failure reason, and when it last changed), filtered by status with counts in the URL; a row opens the assessment page, and the empty state leads to uploading a document and starting an assessment.
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { ClipboardCheckIcon, FileUpIcon, PlusIcon, SearchIcon, TriangleAlertIcon } from "lucide-react";
import { useMemo } from "react";

import type { AssessmentSummary } from "@/api/__generated__";
import { DataTable, dataTableColumnHelper, type DataTableColumn } from "@/components/common/DataTable";
import { EmptyState } from "@/components/common/EmptyState";
import { FacetFilter } from "@/components/common/FacetFilter";
import { ProblemState } from "@/components/common/ProblemState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { linkClass } from "@/lib/cn";
import { plural } from "@/lib/format";

import {
  ASSESSMENT_STAGES,
  assessmentStage,
  gridCodeLabel,
  plantTypeLabel,
  STAGE_FILTER_LABELS,
  type AssessmentStage,
} from "./labels";
import { assessmentsQueryOptions } from "./queries";
import { AssessmentsListedAt, AssessmentStatus, RelativeTime } from "./StatusChips";

export const NO_ASSESSMENTS = "No assessments yet. Add a document to compare with the grid code.";

const column = dataTableColumnHelper<AssessmentSummary>();

export function NewerVersionChip() {
  return (
    <Badge variant="grey" className="w-fit">
      <TriangleAlertIcon aria-hidden />
      Newer grid-code version
    </Badge>
  );
}

function columns(region: string): DataTableColumn<AssessmentSummary>[] {
  return [
    column.accessor("name", {
      header: "Name",
      cell: ({ row }) => (
        <div className="flex min-w-[220px] flex-col gap-0.5">
          <Link
            to="/$region/assessments/$assessmentUid"
            params={{ region, assessmentUid: row.original.assessment_uid }}
            className={`${linkClass} font-semibold`}
            tabIndex={-1}
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            {row.original.name}
          </Link>
          <span className="text-xs text-muted-foreground">{`by ${row.original.created_by_name}`}</span>
        </div>
      ),
    }) as DataTableColumn<AssessmentSummary>,
    column.accessor((row) => row.capability_document.title, {
      id: "product",
      header: "Product",
      cell: (info) => (
        <span className="line-clamp-2 max-w-[260px]" title={info.getValue()}>
          {info.getValue()}
        </span>
      ),
    }) as DataTableColumn<AssessmentSummary>,
    column.accessor((row) => gridCodeLabel(row.grid_code), {
      id: "grid_code",
      header: "Grid code",
      cell: ({ row, getValue }) => (
        <div className="flex flex-col gap-1">
          <span className="whitespace-nowrap">{getValue()}</span>
          {row.original.newer_version_available ? <NewerVersionChip /> : null}
        </div>
      ),
    }) as DataTableColumn<AssessmentSummary>,
    column.accessor("plant_type", {
      header: "Plant type",
      cell: (info) => <span className="whitespace-nowrap">{plantTypeLabel(info.getValue())}</span>,
    }) as DataTableColumn<AssessmentSummary>,
    column.display({
      id: "status",
      header: "Status",
      cell: ({ row }) => <AssessmentStatus assessment={row.original} />,
    }),
    column.accessor("updated_at", {
      header: "Updated",
      cell: (info) => <RelativeTime value={info.getValue()} className="text-chip text-ink-2" />,
    }) as DataTableColumn<AssessmentSummary>,
  ];
}

interface AssessmentsTableProps {
  region: string;
  jurisdiction: string;
  stages: readonly AssessmentStage[];
  onStagesChange: (stages: AssessmentStage[]) => void;
  onUpload: () => void;
  uploadDisabled: boolean;
}

export function AssessmentsTable({
  region,
  jurisdiction,
  stages,
  onStagesChange,
  onUpload,
  uploadDisabled,
}: AssessmentsTableProps) {
  const assessments = useQuery(assessmentsQueryOptions(jurisdiction));
  const navigate = useNavigate();
  const tableColumns = useMemo(() => columns(region), [region]);
  const all = assessments.data ?? [];
  const rows = stages.length === 0 ? all : all.filter((item) => stages.includes(assessmentStage(item)));
  const counts = (stage: AssessmentStage) => all.filter((item) => assessmentStage(item) === stage).length;

  const body = (() => {
    if (assessments.error !== null) {
      return <ProblemState problem={assessments.error} retry={() => void assessments.refetch()} />;
    }
    if (assessments.isPending) {
      return (
        <div aria-hidden className="flex flex-col gap-2 p-4">
          {[0, 1, 2, 3].map((index) => (
            <Skeleton key={index} className="h-12 w-full" />
          ))}
        </div>
      );
    }
    if (all.length === 0) {
      return (
        <EmptyState
          icon={ClipboardCheckIcon}
          title="No assessments yet"
          description="Add a document to compare with the grid code."
          action={
            <div className="flex flex-wrap justify-center gap-2">
              <Button variant="outline" disabled={uploadDisabled} onClick={onUpload}>
                <FileUpIcon aria-hidden />
                Add document
              </Button>
              <Button asChild>
                <Link to="/$region/assessments/new" params={{ region }}>
                  <PlusIcon aria-hidden />
                  New assessment
                </Link>
              </Button>
            </div>
          }
        />
      );
    }
    if (rows.length === 0) {
      return (
        <EmptyState
          icon={SearchIcon}
          title="No assessments match this filter"
          action={
            <Button
              variant="outline"
              onClick={() => {
                onStagesChange([]);
              }}
            >
              Clear filter
            </Button>
          }
        />
      );
    }
    return (
      <AssessmentsListedAt value={assessments.dataUpdatedAt}>
        <DataTable
          data={rows}
          columns={tableColumns}
          getRowId={(row) => row.assessment_uid}
          caption="Assessments"
          rowHeight={72}
          onRowClick={(row) => {
            void navigate({
              to: "/$region/assessments/$assessmentUid",
              params: { region, assessmentUid: row.assessment_uid },
            });
          }}
          className="max-h-[calc(100dvh-280px)] min-h-[240px]"
        />
      </AssessmentsListedAt>
    );
  })();

  return (
    <Card className="gap-0 overflow-hidden p-0">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4.5 py-3">
        <p className="m-0 text-chip text-muted-foreground">
          {assessments.data === undefined
            ? " "
            : stages.length === 0
              ? plural(all.length, "assessment")
              : `${plural(rows.length, "assessment")} of ${String(all.length)}`}
        </p>
        <FacetFilter
          label="Status"
          sections={[
            {
              key: "status",
              title: "Status",
              options: ASSESSMENT_STAGES.filter((stage) => counts(stage) > 0 || stages.includes(stage)).map(
                (stage) => ({ value: stage, label: STAGE_FILTER_LABELS[stage], count: counts(stage) }),
              ),
              selected: [...stages],
              onChange: (values) => {
                onStagesChange(values as AssessmentStage[]);
              },
            },
          ]}
          onClearAll={() => {
            onStagesChange([]);
          }}
        />
      </div>
      {body}
    </Card>
  );
}
